import { useState } from 'react'
import type { FC } from 'react'
import { PatientUser, ValueSetter } from '~/shared/types'
import { Button, Box, Grid, Typography } from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useFormik } from 'formik'

import { EditField } from '~/shared/ui'
import EditPersonFields from './EditPersonFields'
import PhoneNumberChangeModal from './PhoneNumberChangeModal'
import EmailChangeModel from './EmailChangeModel'

interface EditPatientFormProps {
  patient: PatientUser
  loading: boolean
  handleSubmit: (values: PatientUser) => void | Promise<void>
}

const EditPatientForm: FC<EditPatientFormProps> = props => {
  const [showPhoneNumberChangeModal, setShowPhoneNumberChangeModal] = useState(false)
  const [showEmailChangeModel, setShowEmailChangeModel] = useState(false)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: props.patient,
    onSubmit: props.handleSubmit,
  })

  const {
    first_name: firstName = '',
    last_name: lastName = '',
    email = '',
    phone_number: phoneNumber = '',
    person,
  } = formik.values
  const {
    dob = '',
    preferred_name: preferredName = '',
    sex = 'Unknown',
    employer = null,
    gender = null,
    pronouns = null,
  } = person

  // Shortcut for change handlers and values
  const setValues: ValueSetter = (name, value) => ({
    name,
    onChange: e => {
      e.persist()
      formik.handleChange(e)
      void formik.setFieldTouched(name, true, false)
    },
    disabled: props.loading,
    value,
  })

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <EditPersonFields
          first_name={firstName}
          last_name={lastName}
          dob={dob ?? ''}
          preferred_name={preferredName ?? ''}
          employer={employer}
          gender={Array.isArray(gender) ? gender.filter((g): g is string => g != null) : null}
          sex={sex ?? 'Unknown'}
          pronouns={pronouns}
          loading={props.loading}
          isUser={true}
          setValues={setValues}
          setFieldValue={formik.setFieldValue}
          setFieldTouched={name => void formik.setFieldTouched(name)}
          memberIdentifierType={props.patient.person.member_identifier_type}
          patientId={props.patient.person.id}
        />
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <EditField
            {...setValues('email', email)}
            onChangeButtonClick={() => setShowEmailChangeModel(true)}
            label="Email"
            variant="outlined"
            showChangeButton={true}
            slotProps={{ input: { readOnly: true } }}
          />

          <EditField
            {...setValues('phone_number', phoneNumber)}
            onChangeButtonClick={() => {
              setShowPhoneNumberChangeModal(true)
            }}
            label="Phone Number"
            variant="outlined"
            showChangeButton={true}
            disabled={true}
            slotProps={{ input: { readOnly: true } }}
          />
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3 }}>
          {props.patient.person.request_auth_for_phi ? <VisibilityOff /> : <Visibility />}

          <Box sx={{ ml: 1 }}>
            <Typography variant="body2">
              Patient is {props.patient.person.request_auth_for_phi ? '' : 'not '} VIP
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 3 }}>
          {formik.dirty && (
            <Box sx={{ mr: 3 }}>
              <Button onClick={() => formik.resetForm()}>Cancel Patient Detail Changes</Button>
            </Box>
          )}

          <Button type="submit" variant="outlined" color="primary">
            Save Patient Details
          </Button>
        </Box>
      </form>

      <Box>
        {showPhoneNumberChangeModal && (
          <PhoneNumberChangeModal
            handleClose={() => setShowPhoneNumberChangeModal(false)}
            open={showPhoneNumberChangeModal}
          />
        )}
      </Box>
      <Box>
        {showEmailChangeModel && (
          <EmailChangeModel
            handleClose={() => setShowEmailChangeModel(false)}
            open={showEmailChangeModel}
            patient={props.patient}
          />
        )}
      </Box>
    </>
  )
}

export default EditPatientForm
